// CashCalculatorPopup.tsx
import React from 'react';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import './CashCalculatorPopup.css';

export interface BillCount {
    denomination: number;
    count: number;
    label: string;
}

interface CashCalculatorPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (total: number) => void;
    bills: BillCount[];
    setBills: React.Dispatch<React.SetStateAction<BillCount[]>>;
    looseCoins: number;
    setLooseCoins: React.Dispatch<React.SetStateAction<number>>;
}

const CashCalculatorPopup: React.FC<CashCalculatorPopupProps> = ({ 
    isOpen, 
    onClose, 
    onApply,
    bills,
    setBills,
    looseCoins,
    setLooseCoins
}) => {
    const handleBillCountChange = (denomination: number, count: number) => {
        setBills(prevBills =>
            prevBills.map(bill =>
                bill.denomination === denomination
                    ? { ...bill, count: Math.max(0, count) }
                    : bill
            )
        );
    };

    const getBillTotal = (denomination: number, count: number): string => {
        return (denomination * count).toFixed(2);
    };

    const getTotalCounted = (): number => {
        const billsTotal = bills.reduce((sum, bill) => sum + (bill.denomination * bill.count), 0);
        return billsTotal + looseCoins;
    };

    const handleApply = () => {
        onApply(getTotalCounted());
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="cash-calculator-overlay" onClick={onClose}></div>
            <div className="cash-calculator-popup">
                <div className="cash-calculator-header">
                    <div className="cash-calculator-title">
                        <span>Cash Calculator</span>
                    </div>
                    <button className="cash-calculator-close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 5L5 15M5 5L15 15" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="cash-calculator-content">
                    <div className="bills-grid">
                        {bills.map((bill) => (
                            <div key={bill.denomination} className="bill-row">
                                <label className="bill-label">{getCurrencySymbol()}{bill.label}</label>
                                <div className="bill-input-group">
                                    <input
                                        type="number"
                                        min="0"
                                        value={bill.count}
                                        onChange={(e) => handleBillCountChange(bill.denomination, parseInt(e.target.value) || 0)}
                                        className="bill-count-input"
                                    />
                                    <span className="bill-total">{formatPrice(getBillTotal(bill.denomination, bill.count))}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="loose-coins-section">
                        <label className="bill-label">LOOSE COINS (TOTAL VALUE)</label>
                        <div className="loose-coins-input-wrapper">
                            <span className="currency-prefix">{getCurrencySymbol()}</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={looseCoins}
                                onChange={(e) => setLooseCoins(parseFloat(e.target.value) || 0)}
                                className="loose-coins-input"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    <div className="total-section">
                        <span className="total-label">Total Counted</span>
                        <span className="total-value">{formatPrice(getTotalCounted())}</span>
                    </div>

                    <button className="blue-filled-btn" onClick={handleApply} style={{minHeight: 48, width: '100%', borderRadius: 8}}>
                        Apply Total to Z-Report
                    </button>
                </div>
            </div>
        </>
    );
};

export default CashCalculatorPopup;